import React, { memo, useEffect, useState, useCallback } from 'react';
import { Wrapper, ControlledPanel } from './MAccordionItem.styles';
import { type MAccordionItemProps } from './MAccordionItem.types';
import { MAccordionControl } from '../MAccordionControl';
import MSlot from '../MSlot';
import { useAccordionContext } from '../../organisms/OAccordion';

const COMPONENT_NAME = 'm-web-ui-kit-MAccordionItem';
const TEST_ID = 'web-ui-kit-MAccordionItem';

const MAccordionItem = memo(function MAccordionItem({ id, mAccordionControl, content, ...rest }: MAccordionItemProps) {
  const { idBase, expandedSet, setExpandedIds, registerItemId, unregisterItemId, unmountOnCollapse } =
    useAccordionContext();

  useEffect(() => {
    registerItemId(id);
    return () => unregisterItemId(id);
  }, [id, registerItemId, unregisterItemId]);

  const isExpanded = expandedSet.has(id);
  const buttonId = `${idBase}-btn-${id}`;
  const panelId = `${idBase}-panel-${id}`;

  const [hasBeenExpanded, setHasBeenExpanded] = useState(false);

  useEffect(() => {
    if (isExpanded) setHasBeenExpanded(true);
  }, [isExpanded]);

  const toggle = useCallback(() => {
    const next = new Set(expandedSet);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExpandedIds(Array.from(next));
  }, [expandedSet, id, setExpandedIds]);

  return (
    <Wrapper data-component-name={COMPONENT_NAME} data-testid={TEST_ID} {...rest}>
      <MAccordionControl
        {...mAccordionControl}
        id={buttonId}
        aria-controls={panelId}
        aria-expanded={isExpanded}
        onClick={toggle}
        icon={isExpanded ? 'icon-arrow-up' : 'icon-arrow-down'}
      />
      {unmountOnCollapse ? (
        isExpanded ? (
          <ControlledPanel id={panelId} role="region" aria-labelledby={buttonId} aria-live="polite">
            <MSlot {...content} />
          </ControlledPanel>
        ) : null
      ) : hasBeenExpanded || isExpanded ? (
        <ControlledPanel id={panelId} role="region" aria-labelledby={buttonId} hidden={!isExpanded} aria-live="polite">
          <MSlot {...content} />
        </ControlledPanel>
      ) : null}
    </Wrapper>
  );
});

export { MAccordionItem, TEST_ID as M_ACCORDION_ITEM_TEST_ID };
